import React, { useEffect, useState } from 'react';
import { db, collection, onSnapshot, addDoc, updateDoc, doc, Timestamp, query, orderBy, handleFirestoreError, OperationType } from '../firebase';
import { useAuth } from '../App';
import { 
  ShoppingBag, 
  Plus, 
  Trash2, 
  Edit2, 
  X, 
  Eye, 
  Download, 
  Search, 
  Loader2, 
  Building2, 
  Receipt, 
  FileText, 
  TrendingUp, 
  User, 
  Calendar,
  CheckCircle2,
  AlertTriangle,
  FolderLock,
  Printer,
  List,
  Grid
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { downloadFile } from '../lib/downloadHelper';
import { SupplierPerformanceAnalytics } from '../components/SupplierPerformanceAnalytics';
import { LPOPrintModal } from '../components/LPOPrintModal';

interface Supplier {
  id: string;
  name: string;
  tinNumber: string; // Taxpayer Identification Number
  vrnNumber?: string; // VAT Registration Number
  email: string;
  phone: string;
  address: string;
  createdAt: Timestamp;
}

interface Requisition {
  id: string;
  refNumber: string;
  requestedBy: string;
  department: string;
  date: Timestamp;
  itemDescription: string;
  quantity: number;
  estimatedCost: number;
  purpose: string;
  status: 'pending' | 'approved' | 'rejected' | 'lpo_created';
  createdAt: Timestamp;
}

interface LPOItem {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  amount: number;
}

interface LPO {
  id: string;
  lpoNumber: string;
  supplierId: string;
  supplierName: string;
  supplierAddress?: string;
  supplierBox?: string;
  supplierTin: string;
  supplierVrn?: string;
  customerName?: string;
  jobCardNo?: string;
  requiredByDate?: string;
  currency: 'TZS' | 'USD';
  items: LPOItem[];
  subtotal: number;
  vatAmount: number; // 18% VAT
  totalAmount: number;
  deliveryTerms: string;
  paymentTerms: string;
  preparedBy: string;
  status: 'draft' | 'pending_hod' | 'pending_accountant' | 'pending_finance' | 'pending_md' | 'approved' | 'sent' | 'cancelled';
  createdAt: Timestamp;
  hodSignature?: {
    signedBy: string;
    signedAt: Timestamp;
    signatureText: string;
  };
  accountantSignature?: {
    signedBy: string;
    signedAt: Timestamp;
    signatureText: string;
  };
  financeSignature?: {
    signedBy: string;
    signedAt: Timestamp;
    signatureText: string;
  };
  mdSignature?: {
    signedBy: string;
    signedAt: Timestamp;
    signatureText: string;
  };
}

const ProcurementPage: React.FC = () => {
  const { profile, isAdmin, isManager, isStore } = useAuth();
  
  // Checking access permissions (Procurement Officer, Admin, Manager, Store)
  const isProcurementUser = profile?.role === 'procurement' || isAdmin || isManager || isStore;

  // Active sub-dashboard tab
  const [activeTab, setActiveTab] = useState<'lpo' | 'requisitions' | 'suppliers' | 'analytics'>('lpo');

  // Firestore Lists
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [requisitions, setRequisitions] = useState<Requisition[]>([]);
  const [lpos, setLpos] = useState<LPO[]>([]);
  const [loading, setLoading] = useState(true);

  // Search filter
  const [searchTerm, setSearchTerm] = useState('');

  // Modals controllers
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [isReqModalOpen, setIsReqModalOpen] = useState(false);
  const [isLpoModalOpen, setIsLpoModalOpen] = useState(false);
  const [selectedLpo, setSelectedLpo] = useState<LPO | null>(null);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printLpo, setPrintLpo] = useState<LPO | null>(null);

  // Form states - Supplier
  const [supName, setSupName] = useState('');
  const [supTin, setSupTin] = useState('');
  const [supVrn, setSupVrn] = useState('');
  const [supEmail, setSupEmail] = useState('');
  const [supPhone, setSupPhone] = useState('');
  const [supAddress, setSupAddress] = useState('');

  // Form states - Requisition
  const [reqDescription, setReqDescription] = useState('');
  const [reqQuantity, setReqQuantity] = useState<number>(1);
  const [reqEstCost, setReqEstCost] = useState<number>(0);
  const [reqPurpose, setReqPurpose] = useState('');

  // Form states - LPO Builder
  const [lpoSupId, setLpoSupId] = useState('');
  const [lpoCustomerName, setLpoCustomerName] = useState('BRIGHT HQ');
  const [lpoJobCardNo, setLpoJobCardNo] = useState('');
  const [lpoRequiredByDate, setLpoRequiredByDate] = useState(new Date().toISOString().split('T')[0]);
  const [lpoCurrency, setLpoCurrency] = useState<'TZS' | 'USD'>('TZS');
  const [lpoItems, setLpoItems] = useState<LPOItem[]>([{ description: '', quantity: 1, unit: 'Pcs', unitPrice: 0, amount: 0 }]);
  const [lpoDelivery, setLpoDelivery] = useState('Immediate Delivery to BTS Warehouse');
  const [lpoPayment, setLpoPayment] = useState('CASH');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lpoSignatureText, setLpoSignatureText] = useState('');
  const [stockItems, setStockItems] = useState<any[]>([]);

  // Load Firestore data
  useEffect(() => {
    // 1. Listen Suppliers
    const unsubSuppliers = onSnapshot(collection(db, 'procurement_suppliers'), (snapshot) => {
      const list: Supplier[] = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() } as Supplier);
      });
      setSuppliers(list.sort((a, b) => a.name.localeCompare(b.name)));
    });

    // 2. Listen Requisitions
    const unsubReqs = onSnapshot(
      query(collection(db, 'procurement_requisitions'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const list: Requisition[] = [];
        snapshot.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() } as Requisition);
        });
        setRequisitions(list);
      }
    );

    // 3. Listen LPOs
    const unsubLpos = onSnapshot(
      query(collection(db, 'procurement_lpos'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const list: LPO[] = [];
        snapshot.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() } as LPO);
        });
        setLpos(list);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading LPOs:', err);
        setLoading(false);
      }
    );

    // 4. Listen Stock Items for Low Stock Alerts
    const unsubStock = onSnapshot(collection(db, 'stock_items'), (snapshot) => {
      const list: any[] = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      setStockItems(list);
    });

    return () => {
      unsubSuppliers();
      unsubReqs();
      unsubLpos();
      unsubStock();
    };
  }, []);

  // --- Calculations ---
  const calculateLPOItemAmount = (qty: number, price: number) => {
    return Number((qty * price).toFixed(2));
  };

  const handleLPOItemChange = (index: number, key: keyof LPOItem, value: any) => {
    const updated = [...lpoItems];
    if (key === 'quantity' || key === 'unitPrice') {
      const valNum = value === '' ? 0 : Number(value);
      updated[index][key] = valNum as never;
      updated[index].amount = calculateLPOItemAmount(updated[index].quantity, updated[index].unitPrice);
    } else {
      updated[index][key] = value as never;
    }
    setLposDraftAndFormula(updated);
  };

  const setLposDraftAndFormula = (itemsList: LPOItem[]) => {
    setLpoItems(itemsList);
  };

  const addLPOItemLine = () => {
    setLpoItems([...lpoItems, { description: '', quantity: 1, unit: 'Pcs', unitPrice: 0, amount: 0 }]);
  };

  const removeLPOItemLine = (index: number) => {
    if (lpoItems.length === 1) return;
    setLpoItems(lpoItems.filter((_, idx) => idx !== index));
  };

  const subtotalAmount = lpoItems.reduce((acc, item) => acc + item.amount, 0);
  const vatAmount = Number((subtotalAmount * 0.18).toFixed(2)); // Tanzania 18% VAT Act
  const lpoTotalAmount = subtotalAmount + vatAmount;

  // --- Form Submissions ---

  // 1. Supplier Submit
  const handleSupplierSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supName || !supTin) {
      toast.error('Supplier name and TIN are required');
      return;
    }

    try {
      await addDoc(collection(db, 'procurement_suppliers'), {
        name: supName.trim(),
        tinNumber: supTin.trim(),
        vrnNumber: supVrn.trim(),
        email: supEmail.trim(),
        phone: supPhone.trim(),
        address: supAddress.trim(),
        createdAt: Timestamp.now()
      });
      toast.success('Supplier registered successfully!');
      setIsSupplierModalOpen(false);
      // Reset Form
      setSupName('');
      setSupTin('');
      setSupVrn('');
      setSupEmail('');
      setSupPhone('');
      setSupAddress('');
    } catch (e) {
      console.error('Error adding supplier:', e);
      toast.error('Failed to register supplier');
    }
  };

  // 2. Requisition Submit
  const handleRequisitionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reqDescription) {
      toast.error('Item Description is required');
      return;
    }

    try {
      const refNumber = `REQ/BTS/${new Date().getFullYear()}/${Math.floor(1000 + Math.random() * 9000)}`;
      await addDoc(collection(db, 'procurement_requisitions'), {
        refNumber,
        requestedBy: profile?.displayName || profile?.email || 'Technical Staff',
        department: profile?.role?.toUpperCase() || 'TECHNICAL',
        date: Timestamp.now(),
        itemDescription: reqDescription.trim(),
        quantity: reqQuantity,
        estimatedCost: reqEstCost,
        purpose: reqPurpose.trim(),
        status: 'pending',
        createdAt: Timestamp.now()
      });
      toast.success('Requisition requisition raised successfully!');
      setIsReqModalOpen(false);
      setReqDescription('');
      setReqQuantity(1);
      setReqEstCost(0);
      setReqPurpose('');
    } catch (e) {
      console.error('Error adding requisition:', e);
      toast.error('Failed to submit requisition');
    }
  };

  // 3. LPO Submit
  const handleLPOSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lpoSupId) {
      toast.error('Please select a supplier');
      return;
    }
    if (lpoItems.some(item => !item.description || item.unitPrice <= 0)) {
      toast.error('Please fill in complete item descriptions and prices');
      return;
    }

    const supplier = suppliers.find(s => s.id === lpoSupId);
    if (!supplier) return;

    setIsSubmitting(true);
    try {
      const lpoNumber = `LPO/BTSGS/${new Date().getFullYear()}/${101 + lpos.length}`;
      await addDoc(collection(db, 'procurement_lpos'), {
        lpoNumber,
        supplierId: lpoSupId,
        supplierName: supplier.name,
        supplierAddress: supplier.address || 'DAR ES SALAAM.',
        supplierBox: '2534',
        supplierTin: supplier.tinNumber,
        supplierVrn: supplier.vrnNumber || '',
        customerName: lpoCustomerName.trim() || 'BRIGHT HQ',
        jobCardNo: lpoJobCardNo.trim() || 'N/A',
        requiredByDate: lpoRequiredByDate || new Date().toLocaleDateString('en-GB'),
        currency: lpoCurrency,
        items: lpoItems,
        subtotal: subtotalAmount,
        vatAmount,
        totalAmount: lpoTotalAmount,
        deliveryTerms: lpoDelivery,
        paymentTerms: lpoPayment,
        preparedBy: profile?.displayName || profile?.email || 'Purchasing Officer',
        status: 'pending_hod', // Stage 1: Pending HOD review
        createdAt: Timestamp.now()
      });

      toast.success(`LPO ${lpoNumber} drafted successfully! Pending HOD review.`);
      setIsLpoModalOpen(false);
      // Reset LPO builder form
      setLpoSupId('');
      setLpoJobCardNo('');
      setLpoItems([{ description: '', quantity: 1, unit: 'Pcs', unitPrice: 0, amount: 0 }]);
    } catch (e) {
      console.error('Error generating LPO:', e);
      handleFirestoreError(e, OperationType.CREATE, 'procurement_lpos');
    } finally {
      setIsSubmitting(false);
    }
  };

  // LPO Multi-Stage Approval Handlers (HOD -> Chief Accountant -> Managing Director)
  const handleLPOApprove = async (lpo: LPO, stage: 'hod' | 'accountant' | 'md') => {
    if (!lpoSignatureText.trim()) {
      toast.error('Please enter your full name as digital signature');
      return;
    }

    try {
      const lpoRef = doc(db, 'procurement_lpos', lpo.id);
      const signatureObj = {
        signedBy: profile?.displayName || profile?.email || stage.toUpperCase(),
        signedAt: Timestamp.now(),
        signatureText: lpoSignatureText.trim()
      };

      if (stage === 'hod') {
        await updateDoc(lpoRef, {
          status: 'pending_accountant',
          hodSignature: signatureObj
        });
        toast.success('HOD Approval recorded. LPO forwarded to Chief Accountant!');
      } else if (stage === 'accountant') {
        await updateDoc(lpoRef, {
          status: 'pending_md',
          accountantSignature: signatureObj,
          financeSignature: signatureObj
        });
        toast.success('Chief Accountant Approval recorded. LPO forwarded to Managing Director!');
      } else if (stage === 'md') {
        await updateDoc(lpoRef, {
          status: 'approved',
          mdSignature: signatureObj
        });
        toast.success('Managing Director Authorization complete! LPO is fully approved.');
      }
      setSelectedLpo(null);
      setLpoSignatureText('');
    } catch (e) {
      console.error('Error recording approval:', e);
      toast.error('Failed to submit digital signature approval');
    }
  };

  const handleLPODecline = async (lpo: LPO) => {
    try {
      await updateDoc(doc(db, 'procurement_lpos', lpo.id), {
        status: 'cancelled'
      });
      toast.success('LPO has been marked as cancelled/declined');
      setSelectedLpo(null);
    } catch (e) {
      console.error('Error cancelling LPO:', e);
      toast.error('Failed to decline LPO');
    }
  };

  // --- PDF LPO Exporter ---
  const handleExportLPO_PDF = (lpo: LPO) => {
    const doc = new jsPDF();
    
    // Formal Contract Frame
    doc.setDrawColor(30, 58, 138);
    doc.setLineWidth(0.5);
    doc.rect(5, 5, 200, 287);

    // Letterhead
    doc.setFillColor(30, 58, 138);
    doc.rect(5, 5, 200, 42, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('BRIGHT TECHNICAL SYSTEMS', 15, 18);

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(249, 115, 22); // Orange Accent
    doc.text('General Supplies Limited | GPSA Registered Contractor & Supplier', 15, 24);
    doc.setTextColor(255, 255, 255);
    doc.text('TIN No: 102-455-890 | VRN: 40012589-Z', 15, 30);
    doc.text('Dar es Salaam Warehouse, Tanzania | info@brighttechnical.co.tz', 15, 36);

    // Document LPO Header Details
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text('LOCAL PURCHASE ORDER (LPO)', 15, 60);

    // Metadata details
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    
    doc.text('LPO Ref No:', 15, 71);
    doc.setFont('helvetica', 'bold');
    doc.text(lpo.lpoNumber, 42, 71);
    
    doc.setFont('helvetica', 'normal');
    doc.text('Date Created:', 15, 77);
    doc.setFont('helvetica', 'bold');
    doc.text(lpo.createdAt?.toDate?.()?.toLocaleDateString() || new Date().toLocaleDateString(), 42, 77);

    doc.setFont('helvetica', 'normal');
    doc.text('Prepared By:', 15, 83);
    doc.setFont('helvetica', 'bold');
    doc.text(lpo.preparedBy, 42, 83);

    // Supplier Info Card (Right)
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(110, 53, 90, 36, 3, 3, 'FD');
    doc.setDrawColor(226, 232, 240);
    
    doc.setTextColor(30, 58, 138);
    doc.setFont('helvetica', 'bold');
    doc.text('SUPPLIER CONTRACTOR', 115, 60);

    doc.setTextColor(15, 23, 42);
    doc.setFontSize(8.5);
    doc.text(`Name: ${lpo.supplierName}`, 115, 66);
    doc.text(`TIN Number: ${lpo.supplierTin}`, 115, 71);
    doc.text(`VRN Number: ${lpo.supplierVrn || 'N/A'}`, 115, 76);
    doc.text(`Address/Deliv: ${lpo.supplierName.substring(0, 15)} HQ`, 115, 81);

    // LPO Items Table
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('PROCUREMENT BILL OF QUANTITIES', 15, 102);

    const itemsHeaders = [['S/N', 'Item / Service Description', 'Qty', 'Unit', 'Unit Price', 'Amount']];
    const itemsBody = lpo.items.map((item, idx) => [
      idx + 1,
      item.description,
      item.quantity,
      item.unit,
      `${lpo.currency} ${item.unitPrice.toLocaleString()}`,
      `${lpo.currency} ${item.amount.toLocaleString()}`
    ]);

    autoTable(doc, {
      startY: 107,
      head: itemsHeaders,
      body: itemsBody,
      theme: 'grid',
      headStyles: { fillColor: [30, 58, 138], fontSize: 9 },
      styles: { fontSize: 8.5, cellPadding: 3.5 },
      columnStyles: {
        1: { cellWidth: 80 },
        2: { halign: 'center' },
        3: { halign: 'center' },
        4: { halign: 'right' },
        5: { halign: 'right' },
      }
    });

    // Totals calculations block
    const finalY = (doc as any).lastAutoTable.finalY + 10;
    doc.setFillColor(250, 250, 250);
    doc.roundedRect(120, finalY, 80, 28, 2, 2, 'FD');
    
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    
    doc.text('Subtotal Amount:', 125, finalY + 6);
    doc.text('VAT Rate (18%):', 125, finalY + 13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 138);
    doc.text('GRAND TOTAL:', 125, finalY + 21);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(`${lpo.currency} ${lpo.subtotal.toLocaleString()}`, 195, finalY + 6, { align: 'right' });
    doc.text(`${lpo.currency} ${lpo.vatAmount.toLocaleString()}`, 195, finalY + 13, { align: 'right' });
    doc.setTextColor(249, 115, 22);
    doc.text(`${lpo.currency} ${lpo.totalAmount.toLocaleString()}`, 195, finalY + 21, { align: 'right' });

    // Regulatory Procurement terms
    const termsY = finalY + 36;
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.text('LEGAL & DELIVERY TERMS', 15, termsY);
    
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'normal');
    doc.text(`1. Delivery Terms: ${lpo.deliveryTerms}`, 15, termsY + 6);
    doc.text(`2. Payment Terms: ${lpo.paymentTerms}`, 15, termsY + 11);
    doc.text('3. Compliance: Generated in compliance with the Public Procurement Regulatory Authority (PPRA) of Tanzania.', 15, termsY + 16);

    // Signatory Panel (Static Base Contract section)
    const sigY = 245;
    doc.line(15, sigY, 75, sigY);
    doc.line(135, sigY, 195, sigY);

    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text('Prepared By: Procurement Officer', 15, sigY + 5);
    doc.text('Signature & Stamp', 15, sigY + 10);

    doc.text('Authorized Signatory: Managing Director', 135, sigY + 5);
    doc.text('Bright Technical Systems Stamp', 135, sigY + 10);

    // Legal base footer
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.setFont('helvetica', 'italic');
    doc.text('E & O.E. | Official Corporate Purchase Order. Valid with official company stamp.', 15, 281);

    downloadFile(doc.output('blob'), `LPO_${lpo.lpoNumber.replace(/\//g, '_')}.pdf`).then(success => {
      if (success) toast.success('LPO contract PDF exported successfully!');
    });
  };

  if (!isProcurementUser) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-6 text-center">
        <FolderLock className="w-16 h-16 text-red-600 mb-4" />
        <h3 className="text-xl font-black text-red-600 mb-2">Access Denied</h3>
        <p className="text-slate-500 font-medium">You do not have permissions to access the Procurement Module.</p>
      </div>
    );
  }

  const filteredSuppliers = suppliers.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.tinNumber.includes(searchTerm)
  );

  return (
    <div className="space-y-8" id="procurement-full-module">
      {/* Upper header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <ShoppingBag className="w-8 h-8 text-blue-900" />
            <span>Procurement Module</span>
          </h1>
          <p className="text-slate-500 font-medium text-sm mt-1">Raise corporate purchase requisitions, build sequential LPOs, and manage suppliers.</p>
        </div>

        {/* Navigation Tab selection */}
        <div className="flex flex-wrap bg-slate-100 p-1.5 rounded-2xl border border-slate-200 self-start lg:self-center gap-1">
          <button
            onClick={() => setActiveTab('lpo')}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center gap-2 cursor-pointer ${activeTab === 'lpo' ? 'bg-white text-blue-900 shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
          >
            <Receipt className="w-4 h-4" />
            <span>Local Purchase Orders (LPO)</span>
          </button>
          <button
            onClick={() => setActiveTab('requisitions')}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center gap-2 cursor-pointer ${activeTab === 'requisitions' ? 'bg-white text-blue-900 shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
          >
            <FileText className="w-4 h-4" />
            <span>Purchase Requisitions</span>
          </button>
          <button
            onClick={() => setActiveTab('suppliers')}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center gap-2 cursor-pointer ${activeTab === 'suppliers' ? 'bg-white text-blue-900 shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
          >
            <Building2 className="w-4 h-4" />
            <span>Suppliers Directory</span>
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center gap-2 cursor-pointer ${activeTab === 'analytics' ? 'bg-white text-blue-900 shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
          >
            <TrendingUp className="w-4 h-4" />
            <span>Supplier Performance</span>
          </button>
        </div>
      </div>

      {activeTab === 'lpo' && (
        <div className="space-y-6">
          {/* Low-Stock Alert Banner */}
          {(() => {
            const lowStock = stockItems.filter(item => item.quantity <= (item.minLevel !== undefined ? item.minLevel : 5));
            if (lowStock.length === 0) return null;
            return (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-[2rem] p-6 shadow-sm space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-amber-500 text-white rounded-xl flex items-center justify-center font-bold">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-black text-amber-900 uppercase tracking-wide">Critical Low-Stock Inventory Warning</h4>
                    <p className="text-slate-500 text-[11px] font-medium mt-0.5">The following items are below their minimum threshold and require immediate reorder procurement.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {lowStock.map((item) => {
                    const quantityNeeded = Math.max((item.minLevel || 5) * 2 - item.quantity, 5);
                    return (
                      <div key={item.id} className="bg-white/80 backdrop-blur-sm border border-amber-100 p-4 rounded-2xl flex items-center justify-between gap-3 shadow-xs">
                        <div className="space-y-1 overflow-hidden flex-1">
                          <div className="flex items-center gap-1.5 overflow-hidden">
                            <span className="text-[10px] font-black text-slate-400 font-mono bg-slate-100 px-1.5 py-0.5 rounded uppercase flex-shrink-0">{item.code || 'NO-CODE'}</span>
                            <span className="text-xs font-black text-slate-800 truncate block">{item.name}</span>
                          </div>
                          <p className="text-[10px] text-slate-500 font-bold">
                            Current Stock: <span className="text-red-600 font-black">{item.quantity}</span> / Min: {item.minLevel || 5} {item.unit || 'pcs'}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setLpoItems([{
                              description: `Reorder of stock item: ${item.name} (${item.code})`,
                              quantity: quantityNeeded,
                              unit: item.unit || 'Pcs',
                              unitPrice: item.price || 0,
                              amount: quantityNeeded * (item.price || 0)
                            }]);
                            setIsLpoModalOpen(true);
                            toast.info(`Pre-filled LPO to order ${quantityNeeded} units of ${item.name}.`);
                          }}
                          className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-black uppercase rounded-xl shadow-xs transition-all flex items-center gap-1 cursor-pointer whitespace-nowrap flex-shrink-0"
                        >
                          <Plus className="w-3 h-3" />
                          <span>Reorder</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* LPO Action Header */}
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-black text-slate-900">Registered Local Purchase Orders</h3>
              <p className="text-xs text-slate-500 font-medium">Multi-step approval required by HOD, Chief Accountant, and Managing Director.</p>
            </div>
            <button
              onClick={() => setIsLpoModalOpen(true)}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold transition-all shadow-md active:scale-95 cursor-pointer text-sm"
            >
              <Plus className="w-5 h-5" />
              <span>Compose New LPO</span>
            </button>
          </div>

          {/* LPO List View (Table Format) */}
          {lpos.length === 0 ? (
            <div className="bg-white rounded-[2.5rem] border border-slate-100 p-12 text-center shadow-sm">
              <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Receipt className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-black text-slate-900 mb-1">No LPOs Generated</h3>
              <p className="text-slate-500 font-medium max-w-sm mx-auto mb-6">Create Local Purchase Orders with 18% VAT calculations conforming to Tanzanian procurement norms.</p>
              <button
                onClick={() => setIsLpoModalOpen(true)}
                className="px-5 py-3 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold transition-all cursor-pointer"
              >
                Add Your First LPO
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">LPO Number</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Supplier Details</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Job / Customer</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Order Amount</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider text-center">3-Stage Approval Progress</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium">
                    {lpos.map((lpo) => {
                      const isFullyApproved = lpo.status === 'approved';
                      const isPendingHOD = lpo.status === 'pending_hod';
                      const isPendingAccountant = lpo.status === 'pending_accountant' || lpo.status === 'pending_finance';
                      const isPendingMD = lpo.status === 'pending_md';
                      const isCancelled = lpo.status === 'cancelled';

                      return (
                        <tr key={lpo.id} className="hover:bg-slate-50/60 transition-colors">
                          <td className="px-6 py-4 text-sm font-black text-blue-900">
                            {lpo.lpoNumber}
                            <span className="text-[10px] text-slate-400 block font-normal">
                              {lpo.createdAt?.toDate ? lpo.createdAt.toDate().toLocaleDateString('en-GB') : 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm font-bold text-slate-900">
                            {lpo.supplierName}
                            <span className="text-[10px] text-slate-400 font-semibold block">TIN: {lpo.supplierTin}</span>
                          </td>
                          <td className="px-6 py-4 text-xs font-bold text-slate-700">
                            {lpo.customerName || 'BRIGHT HQ'}
                            <span className="text-[10px] text-slate-400 font-medium block">Job Card: {lpo.jobCardNo || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4 text-sm font-black text-slate-900">
                            {lpo.currency} {lpo.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-1.5">
                              {/* HOD Stage */}
                              <span
                                className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase border ${
                                  lpo.hodSignature
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : isPendingHOD
                                    ? 'bg-amber-50 text-amber-700 border-amber-200 animate-pulse'
                                    : 'bg-slate-50 text-slate-400 border-slate-100'
                                }`}
                                title={lpo.hodSignature ? `HOD Approved: ${lpo.hodSignature.signatureText}` : 'HOD Review'}
                              >
                                {lpo.hodSignature ? '✓ HOD' : '1. HOD'}
                              </span>

                              {/* Accountant Stage */}
                              <span
                                className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase border ${
                                  lpo.accountantSignature || lpo.financeSignature
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : isPendingAccountant
                                    ? 'bg-blue-50 text-blue-700 border-blue-200 animate-pulse'
                                    : 'bg-slate-50 text-slate-400 border-slate-100'
                                }`}
                                title={(lpo.accountantSignature || lpo.financeSignature) ? `Accountant Approved` : 'Accountant Review'}
                              >
                                {(lpo.accountantSignature || lpo.financeSignature) ? '✓ Accountant' : '2. Accountant'}
                              </span>

                              {/* MD Stage */}
                              <span
                                className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase border ${
                                  lpo.mdSignature
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : isPendingMD
                                    ? 'bg-purple-50 text-purple-700 border-purple-200 animate-pulse'
                                    : isCancelled
                                    ? 'bg-red-50 text-red-700 border-red-200'
                                    : 'bg-slate-50 text-slate-400 border-slate-100'
                                }`}
                                title={lpo.mdSignature ? `MD Approved: ${lpo.mdSignature.signatureText}` : 'MD Authorisation'}
                              >
                                {lpo.mdSignature ? '✓ MD' : isCancelled ? 'Declined' : '3. MD'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {/* Print Button (Required by prompt) */}
                              <button
                                onClick={() => {
                                  setPrintLpo(lpo);
                                  setIsPrintModalOpen(true);
                                }}
                                className="px-3 py-1.5 bg-blue-900 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs active:scale-95 cursor-pointer"
                                title="Print LPO Document Format"
                              >
                                <Printer className="w-3.5 h-3.5 text-blue-200" />
                                <span>Print</span>
                              </button>

                              {/* Review Details & Sign */}
                              <button
                                onClick={() => setSelectedLpo(lpo)}
                                className="p-2 text-slate-500 hover:text-blue-900 hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
                                title="Review Details & Digital Signatures"
                              >
                                <Eye className="w-4.5 h-4.5" />
                              </button>

                              {/* Download PDF */}
                              <button
                                onClick={() => handleExportLPO_PDF(lpo)}
                                className="p-2 text-slate-500 hover:text-orange-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
                                title="Download PDF Contract"
                              >
                                <Download className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'requisitions' && (
        <div className="space-y-6 animate-fadeIn">
          {/* Requisition Action bar */}
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h3 className="text-lg font-black text-slate-900">Internal Material Requisitions</h3>
            <button
              onClick={() => setIsReqModalOpen(true)}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold transition-all shadow-md active:scale-95 cursor-pointer text-sm"
            >
              <Plus className="w-5 h-5" />
              <span>Raise Requisition Request</span>
            </button>
          </div>

          {requisitions.length === 0 ? (
            <div className="bg-white rounded-[2.5rem] border border-slate-100 p-12 text-center shadow-sm">
              <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-black text-slate-900 mb-1">No Requisition Records</h3>
              <p className="text-slate-500 font-medium max-w-sm mx-auto">Requisitions raised by technical team or store officers will show up here.</p>
            </div>
          ) : (
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Requisition Code</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Item Details</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Raised By</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Est Cost (TZS)</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider text-center">Status</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Reason / Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {requisitions.map((req) => (
                      <tr key={req.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4 text-sm font-black text-blue-900">{req.refNumber}</td>
                        <td className="px-6 py-4 text-sm font-bold text-slate-900">
                          {req.itemDescription}
                          <span className="text-xs text-slate-400 font-medium block">Qty: {req.quantity}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 font-bold">
                          {req.requestedBy}
                          <span className="text-[10px] text-slate-400 font-medium block">{req.department}</span>
                        </td>
                        <td className="px-6 py-4 text-sm font-black text-slate-900">
                          {req.estimatedCost ? req.estimatedCost.toLocaleString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${
                            req.status === 'pending' ? 'bg-orange-50 text-orange-700 border-orange-100' :
                            'bg-green-50 text-green-700 border-green-100'
                          }`}>
                            {req.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-slate-500 font-medium max-w-xs truncate">{req.purpose || 'None'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'suppliers' && (
        <div className="space-y-6 animate-fadeIn">
          {/* Supplier Directory Bar */}
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search suppliers by name or TIN..."
                className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-sm"
              />
            </div>
            <button
              onClick={() => setIsSupplierModalOpen(true)}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold transition-all shadow-md active:scale-95 cursor-pointer text-sm"
            >
              <Plus className="w-5 h-5" />
              <span>Register Supplier</span>
            </button>
          </div>

          {suppliers.length === 0 ? (
            <div className="bg-white rounded-[2.5rem] border border-slate-100 p-12 text-center shadow-sm">
              <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-black text-slate-900 mb-1">No Suppliers Registered</h3>
              <p className="text-slate-500 font-medium max-w-sm mx-auto">Register suppliers with their TIN & VRN numbers to start generating corporate contracts.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSuppliers.map((sup) => (
                <div key={sup.id} className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-900 flex items-center justify-center font-black text-sm">
                        {sup.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="text-base font-black text-slate-900 leading-tight">{sup.name}</h4>
                        <span className="text-[10px] text-slate-400 font-bold block mt-0.5">TIN: {sup.tinNumber}</span>
                      </div>
                    </div>

                    <div className="space-y-2 border-t border-slate-50 pt-4">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400 font-bold">VRN:</span>
                        <span className="text-slate-800 font-black">{sup.vrnNumber || 'Not registered'}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400 font-bold">Email:</span>
                        <span className="text-slate-800 font-bold truncate max-w-xs">{sup.email}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400 font-bold">Phone:</span>
                        <span className="text-slate-800 font-black">{sup.phone}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'analytics' && (
        <SupplierPerformanceAnalytics suppliers={suppliers} lpos={lpos} />
      )}

      {/* MODAL 1: Create Supplier */}
      <AnimatePresence>
        {isSupplierModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSupplierModalOpen(false)}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[2.5rem] w-full max-w-md p-8 relative z-10 shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-black text-slate-900">Register Supplier</h3>
                  <p className="text-slate-400 font-medium text-xs mt-0.5">Enter details as per Tanzania Revenue Authority cards.</p>
                </div>
                <button onClick={() => setIsSupplierModalOpen(false)} className="p-2 bg-slate-50 rounded-xl text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSupplierSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Supplier Company Name *</label>
                  <input type="text" required value={supName} onChange={e => setSupName(e.target.value)} placeholder="e.g. Aerotherm Supplies Ltd" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">TRA TIN Number *</label>
                    <input type="text" required value={supTin} onChange={e => setSupTin(e.target.value)} placeholder="9 Digits TIN" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">TRA VRN Number</label>
                    <input type="text" value={supVrn} onChange={e => setSupVrn(e.target.value)} placeholder="VRN registration" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Email Address</label>
                  <input type="email" required value={supEmail} onChange={e => setSupEmail(e.target.value)} placeholder="supplier@gmail.com" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Phone Number</label>
                  <input type="text" required value={supPhone} onChange={e => setSupPhone(e.target.value)} placeholder="e.g. +255 712..." className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Physical Address</label>
                  <input type="text" required value={supAddress} onChange={e => setSupAddress(e.target.value)} placeholder="Kariakoo, Dar es Salaam" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold" />
                </div>

                <div className="flex gap-3 pt-4 border-t border-slate-50">
                  <button type="button" onClick={() => setIsSupplierModalOpen(false)} className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 rounded-2xl font-bold transition-all text-sm">Cancel</button>
                  <button type="submit" className="flex-1 py-3 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold transition-all text-sm">Save Supplier</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL 2: Create Requisition */}
      <AnimatePresence>
        {isReqModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsReqModalOpen(false)}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[2.5rem] w-full max-w-md p-8 relative z-10 shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-black text-slate-900">Raise Requisition</h3>
                  <p className="text-slate-400 font-medium text-xs mt-0.5">Request parts/tools to be purchased for pending client tasks.</p>
                </div>
                <button onClick={() => setIsReqModalOpen(false)} className="p-2 bg-slate-50 rounded-xl text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleRequisitionSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Item Description *</label>
                  <input type="text" required value={reqDescription} onChange={e => setReqDescription(e.target.value)} placeholder="e.g. Copper pipes 3/8 size" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Quantity Required *</label>
                    <input type="number" min="1" required value={reqQuantity} onChange={e => setReqQuantity(Number(e.target.value))} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Est Unit Cost (TZS)</label>
                    <input type="number" min="0" required value={reqEstCost} onChange={e => setReqEstCost(Number(e.target.value))} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Purpose / Reason *</label>
                  <textarea rows={3} required value={reqPurpose} onChange={e => setReqPurpose(e.target.value)} placeholder="Specify task ID, client branch or site usage..." className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-medium text-sm placeholder-slate-400 resize-none" />
                </div>

                <div className="flex gap-3 pt-4 border-t border-slate-50">
                  <button type="button" onClick={() => setIsReqModalOpen(false)} className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 rounded-2xl font-bold transition-all text-sm">Cancel</button>
                  <button type="submit" className="flex-1 py-3 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold transition-all text-sm">Submit Request</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL 3: Compose LPO Builder */}
      <AnimatePresence>
        {isLpoModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsLpoModalOpen(false)}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[2.5rem] w-full max-w-2xl p-8 relative z-10 shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                <div>
                  <h3 className="text-xl font-black text-slate-900">Local Purchase Order (LPO) Builder</h3>
                  <p className="text-slate-400 font-medium text-xs mt-0.5">Compiles legal commercial agreement with standard 18% TRA VAT.</p>
                </div>
                <button onClick={() => setIsLpoModalOpen(false)} className="p-2 bg-slate-50 rounded-xl text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleLPOSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  {/* Select Supplier */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Select Supplier *</label>
                    <select
                      required
                      value={lpoSupId}
                      onChange={e => setLpoSupId(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-700 text-xs"
                    >
                      <option value="">Choose Supplier</option>
                      {suppliers.map(s => (
                        <option key={s.id} value={s.id}>{s.name} (TIN: {s.tinNumber})</option>
                      ))}
                    </select>
                  </div>

                  {/* Customer Name */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Customer Name</label>
                    <input
                      type="text"
                      value={lpoCustomerName}
                      onChange={e => setLpoCustomerName(e.target.value)}
                      placeholder="BRIGHT HQ"
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-700 text-xs"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {/* Job Card No */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Job Card No.</label>
                    <input
                      type="text"
                      value={lpoJobCardNo}
                      onChange={e => setLpoJobCardNo(e.target.value)}
                      placeholder="e.g. 19432"
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-700 text-xs"
                    />
                  </div>

                  {/* Required By Date */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Required By Date</label>
                    <input
                      type="date"
                      value={lpoRequiredByDate}
                      onChange={e => setLpoRequiredByDate(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-700 text-xs"
                    />
                  </div>

                  {/* Currency */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Currency *</label>
                    <select
                      required
                      value={lpoCurrency}
                      onChange={e => setLpoCurrency(e.target.value as any)}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-700 text-xs"
                    >
                      <option value="TZS">Tanzanian Shilling (TZS)</option>
                      <option value="USD">United States Dollar (USD)</option>
                    </select>
                  </div>
                </div>

                {/* Items Builder */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-2 border-b border-slate-50">
                    <h4 className="text-xs font-black text-blue-900 uppercase tracking-widest">Material Breakdown / Bill of Quantities</h4>
                    <button
                      type="button"
                      onClick={addLPOItemLine}
                      className="text-xs font-black text-orange-500 hover:text-orange-600 flex items-center gap-1 cursor-pointer"
                    >
                      <Plus className="w-4.5 h-4.5" />
                      <span>Add Line Item</span>
                    </button>
                  </div>

                  {lpoItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-3.5 items-end bg-slate-50 p-4 rounded-2xl relative">
                      <div className="sm:col-span-5 space-y-1">
                        <label className="text-[8px] font-black text-slate-400 uppercase">Item Description</label>
                        <input
                          type="text"
                          required
                          value={item.description}
                          onChange={e => handleLPOItemChange(idx, 'description', e.target.value)}
                          placeholder="e.g. Gas R22"
                          className="w-full px-3 py-2 bg-white border border-slate-150 rounded-xl font-bold text-xs"
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-1">
                        <label className="text-[8px] font-black text-slate-400 uppercase">Qty</label>
                        <input
                          type="number"
                          min="0.1"
                          step="0.1"
                          required
                          value={item.quantity}
                          onChange={e => handleLPOItemChange(idx, 'quantity', e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-slate-150 rounded-xl font-bold text-xs"
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-1">
                        <label className="text-[8px] font-black text-slate-400 uppercase">U/M (Unit)</label>
                        <input
                          type="text"
                          required
                          value={item.unit}
                          onChange={e => handleLPOItemChange(idx, 'unit', e.target.value)}
                          placeholder="kg, Pcs, Mtr"
                          className="w-full px-3 py-2 bg-white border border-slate-150 rounded-xl font-bold text-xs"
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-1">
                        <label className="text-[8px] font-black text-slate-400 uppercase">Rate (Unit Price)</label>
                        <input
                          type="number"
                          min="0"
                          required
                          value={item.unitPrice}
                          onChange={e => handleLPOItemChange(idx, 'unitPrice', e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-slate-150 rounded-xl font-bold text-xs"
                        />
                      </div>
                      <div className="sm:col-span-1 flex justify-center pb-1">
                        <button
                          type="button"
                          onClick={() => removeLPOItemLine(idx)}
                          className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Terms block */}
                <div className="grid grid-cols-2 gap-6 p-4 bg-blue-50/50 rounded-3xl border border-blue-100">
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black text-blue-900 uppercase tracking-widest">Delivery terms</label>
                    <input type="text" value={lpoDelivery} onChange={e => setLpoDelivery(e.target.value)} className="w-full px-3 py-2 bg-white border border-blue-100 rounded-xl text-xs font-bold" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black text-blue-900 uppercase tracking-widest">Payment terms</label>
                    <input type="text" value={lpoPayment} onChange={e => setLpoPayment(e.target.value)} className="w-full px-3 py-2 bg-white border border-blue-100 rounded-xl text-xs font-bold" />
                  </div>
                </div>

                {/* Subtotals & VAT calculation Breakdown summary */}
                <div className="flex justify-end">
                  <div className="w-72 space-y-2 text-right p-4 bg-slate-50 rounded-2xl">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400 font-bold">Subtotal Amount:</span>
                      <span className="text-slate-800 font-black">{lpoCurrency} {subtotalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400 font-bold">TRA VAT (18%):</span>
                      <span className="text-slate-800 font-black">{lpoCurrency} {vatAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-slate-150 pt-2">
                      <span className="text-blue-900 font-black">LPO GRAND TOTAL:</span>
                      <span className="text-orange-500 font-black">{lpoCurrency} {lpoTotalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex gap-3 pt-4 border-t border-slate-100">
                  <button type="button" onClick={() => setIsLpoModalOpen(false)} className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 rounded-2xl font-bold transition-all text-sm">Cancel</button>
                  <button type="submit" disabled={isSubmitting} className="flex-1 py-3 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold transition-all text-sm flex justify-center items-center gap-2">
                    {isSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        <span>Draft LPO (Send for Approvals)</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL 4: Multi-Stage Approval & Detail View */}
      <AnimatePresence>
        {selectedLpo && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedLpo(null)}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[2.5rem] w-full max-w-2xl p-8 relative z-10 shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">Purchasing Order Detail</span>
                  <h3 className="text-xl font-black text-slate-900">{selectedLpo.lpoNumber}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setPrintLpo(selectedLpo);
                      setIsPrintModalOpen(true);
                    }}
                    className="px-3 py-1.5 bg-blue-900 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                  >
                    <Printer className="w-3.5 h-3.5 text-blue-200" />
                    <span>Print LPO</span>
                  </button>
                  <button onClick={() => setSelectedLpo(null)} className="p-2 bg-slate-50 rounded-xl text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                {/* Info block */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-2xl text-xs">
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Supplier</span>
                    <span className="font-black text-slate-900 block">{selectedLpo.supplierName}</span>
                    <span className="text-[10px] text-slate-500 block">TIN: {selectedLpo.supplierTin}</span>
                  </div>
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Customer / Job Ref</span>
                    <span className="font-bold text-slate-800 block">{selectedLpo.customerName || 'BRIGHT HQ'}</span>
                    <span className="text-[10px] text-slate-500 block">Job Card: {selectedLpo.jobCardNo || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Dates</span>
                    <span className="font-bold text-slate-800 block">Req: {selectedLpo.requiredByDate || 'Immediate'}</span>
                  </div>
                </div>

                {/* Items details */}
                <div className="space-y-3">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Bill of Quantities</h4>
                  <div className="border border-slate-100 rounded-2xl overflow-hidden text-xs">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-100 font-black text-slate-500">
                          <th className="p-3">Description</th>
                          <th className="p-3 text-center">Qty</th>
                          <th className="p-3 text-center">U/M</th>
                          <th className="p-3 text-right">Rate</th>
                          <th className="p-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-bold text-slate-700">
                        {selectedLpo.items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="p-3 font-bold text-slate-900">{item.description}</td>
                            <td className="p-3 text-center">{item.quantity}</td>
                            <td className="p-3 text-center">{item.unit || 'Pcs'}</td>
                            <td className="p-3 text-right">{selectedLpo.currency} {item.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                            <td className="p-3 text-right text-slate-900">{selectedLpo.currency} {item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Summary calculation block */}
                <div className="flex justify-end border-t border-slate-100 pt-4">
                  <div className="w-64 space-y-1.5 text-right text-xs font-bold text-slate-500">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span className="text-slate-800 font-black">{selectedLpo.currency} {selectedLpo.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>TRA VAT (18%):</span>
                      <span className="text-slate-800 font-black">{selectedLpo.currency} {selectedLpo.vatAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-slate-100 font-black text-blue-900">
                      <span>Order Total:</span>
                      <span className="text-orange-500">{selectedLpo.currency} {selectedLpo.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                {/* Multi-Step Approval and Signature block (HOD, Accountant, MD) */}
                <div className="border-t border-slate-100 pt-6 mt-6 space-y-4 text-xs">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-blue-900" />
                    <span>3-Stage Approval & Sign-off Workflow</span>
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* HOD Status Card */}
                    <div className={`p-3.5 rounded-2xl border ${
                      selectedLpo.status === 'pending_hod' 
                        ? 'bg-amber-50/60 border-amber-200 text-amber-900' 
                        : selectedLpo.hodSignature 
                          ? 'bg-emerald-50/50 border-emerald-100 text-emerald-900' 
                          : 'bg-slate-50 border-slate-100 text-slate-400'
                    }`}>
                      <p className="font-black text-[9px] uppercase tracking-widest text-slate-400 mb-1">Stage 1: HOD Review</p>
                      {selectedLpo.hodSignature ? (
                        <div className="space-y-1">
                          <p className="font-bold text-emerald-700 text-[11px]">✓ Approved & Signed</p>
                          <p className="text-[10px] font-black italic font-mono text-emerald-800 bg-white/80 px-2 py-0.5 rounded border border-emerald-100">
                            "{selectedLpo.hodSignature.signatureText}"
                          </p>
                          <p className="text-[9px] text-slate-400">By {selectedLpo.hodSignature.signedBy}</p>
                        </div>
                      ) : selectedLpo.status === 'pending_hod' ? (
                        <div className="space-y-2">
                          <p className="font-bold text-amber-700">Awaiting HOD Signature</p>
                          {(isAdmin || isManager || profile?.role === 'technical' || profile?.role === 'ops_manager' || profile?.role === 'procurement' || profile?.role === 'md') ? (
                            <div className="space-y-1.5">
                              <input 
                                type="text"
                                placeholder="Full Name to Sign..."
                                value={lpoSignatureText}
                                onChange={e => setLpoSignatureText(e.target.value)}
                                className="w-full px-2.5 py-1 bg-white border border-slate-200 rounded-xl text-[10px] font-bold text-slate-700 focus:outline-none focus:border-amber-400"
                              />
                              <div className="flex gap-1">
                                <button 
                                  onClick={() => handleLPOApprove(selectedLpo, 'hod')}
                                  className="flex-1 py-1 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl text-[9px] transition-all cursor-pointer"
                                >
                                  Approve & Sign
                                </button>
                                <button 
                                  onClick={() => handleLPODecline(selectedLpo)}
                                  className="py-1 px-1.5 bg-red-100 hover:bg-red-200 text-red-700 font-bold rounded-xl text-[9px] transition-all cursor-pointer"
                                >
                                  Decline
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-[9px] text-slate-400 italic">Only HODs can sign Stage 1.</p>
                          )}
                        </div>
                      ) : (
                        <p className="font-bold text-slate-400 text-[10px]">Pending Stage 1</p>
                      )}
                    </div>

                    {/* Chief Accountant Status Card */}
                    <div className={`p-3.5 rounded-2xl border ${
                      (selectedLpo.status === 'pending_accountant' || selectedLpo.status === 'pending_finance')
                        ? 'bg-blue-50/60 border-blue-200 text-blue-900' 
                        : (selectedLpo.accountantSignature || selectedLpo.financeSignature)
                          ? 'bg-emerald-50/50 border-emerald-100 text-emerald-900' 
                          : 'bg-slate-50 border-slate-100 text-slate-400'
                    }`}>
                      <p className="font-black text-[9px] uppercase tracking-widest text-slate-400 mb-1">Stage 2: Chief Accountant</p>
                      {(selectedLpo.accountantSignature || selectedLpo.financeSignature) ? (
                        <div className="space-y-1">
                          <p className="font-bold text-emerald-700 text-[11px]">✓ Approved & Signed</p>
                          <p className="text-[10px] font-black italic font-mono text-emerald-800 bg-white/80 px-2 py-0.5 rounded border border-emerald-100">
                            "{(selectedLpo.accountantSignature || selectedLpo.financeSignature)?.signatureText}"
                          </p>
                          <p className="text-[9px] text-slate-400">By {(selectedLpo.accountantSignature || selectedLpo.financeSignature)?.signedBy}</p>
                        </div>
                      ) : (selectedLpo.status === 'pending_accountant' || selectedLpo.status === 'pending_finance') ? (
                        <div className="space-y-2">
                          <p className="font-bold text-blue-700">Awaiting Accountant</p>
                          {(isAdmin || isManager || profile?.role === 'accountant' || profile?.role === 'billing' || profile?.role === 'md') ? (
                            <div className="space-y-1.5">
                              <input 
                                type="text"
                                placeholder="Full Name to Sign..."
                                value={lpoSignatureText}
                                onChange={e => setLpoSignatureText(e.target.value)}
                                className="w-full px-2.5 py-1 bg-white border border-slate-200 rounded-xl text-[10px] font-bold text-slate-700 focus:outline-none focus:border-blue-400"
                              />
                              <div className="flex gap-1">
                                <button 
                                  onClick={() => handleLPOApprove(selectedLpo, 'accountant')}
                                  className="flex-1 py-1 bg-blue-800 hover:bg-blue-900 text-white font-bold rounded-xl text-[9px] transition-all cursor-pointer"
                                >
                                  Approve & Sign
                                </button>
                                <button 
                                  onClick={() => handleLPODecline(selectedLpo)}
                                  className="py-1 px-1.5 bg-red-100 hover:bg-red-200 text-red-700 font-bold rounded-xl text-[9px] transition-all cursor-pointer"
                                >
                                  Decline
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-[9px] text-slate-400 italic">Only Chief Accountant can sign Stage 2.</p>
                          )}
                        </div>
                      ) : (
                        <p className="font-bold text-slate-400 text-[10px]">Awaiting Stage 1</p>
                      )}
                    </div>

                    {/* MD Status Card */}
                    <div className={`p-3.5 rounded-2xl border ${
                      selectedLpo.status === 'pending_md'
                        ? 'bg-purple-50/60 border-purple-200 text-purple-900' 
                        : selectedLpo.mdSignature
                          ? 'bg-emerald-50/50 border-emerald-100 text-emerald-900' 
                          : 'bg-slate-50 border-slate-100 text-slate-400'
                    }`}>
                      <p className="font-black text-[9px] uppercase tracking-widest text-slate-400 mb-1">Stage 3: Managing Director</p>
                      {selectedLpo.mdSignature ? (
                        <div className="space-y-1">
                          <p className="font-bold text-emerald-700 text-[11px]">✓ Authorised & Signed</p>
                          <p className="text-[10px] font-black italic font-mono text-emerald-800 bg-white/80 px-2 py-0.5 rounded border border-emerald-100">
                            "{selectedLpo.mdSignature.signatureText}"
                          </p>
                          <p className="text-[9px] text-slate-400">By {selectedLpo.mdSignature.signedBy}</p>
                        </div>
                      ) : selectedLpo.status === 'pending_md' ? (
                        <div className="space-y-2">
                          <p className="font-bold text-purple-700">Awaiting MD Authorisation</p>
                          {(isAdmin || isManager || profile?.role === 'md') ? (
                            <div className="space-y-1.5">
                              <input 
                                type="text"
                                placeholder="Full Name to Sign..."
                                value={lpoSignatureText}
                                onChange={e => setLpoSignatureText(e.target.value)}
                                className="w-full px-2.5 py-1 bg-white border border-slate-200 rounded-xl text-[10px] font-bold text-slate-700 focus:outline-none focus:border-purple-400"
                              />
                              <div className="flex gap-1">
                                <button 
                                  onClick={() => handleLPOApprove(selectedLpo, 'md')}
                                  className="flex-1 py-1 bg-purple-800 hover:bg-purple-900 text-white font-bold rounded-xl text-[9px] transition-all cursor-pointer"
                                >
                                  Authorise & Sign
                                </button>
                                <button 
                                  onClick={() => handleLPODecline(selectedLpo)}
                                  className="py-1 px-1.5 bg-red-100 hover:bg-red-200 text-red-700 font-bold rounded-xl text-[9px] transition-all cursor-pointer"
                                >
                                  Decline
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-[9px] text-slate-400 italic">Only MD / General Manager can authorise Stage 3.</p>
                          )}
                        </div>
                      ) : (
                        <p className="font-bold text-slate-400 text-[10px]">Awaiting Stage 2</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions footer */}
              <div className="flex gap-3 pt-6 border-t border-slate-100 mt-6">
                <button onClick={() => setSelectedLpo(null)} className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 rounded-2xl font-bold transition-all text-sm">Close</button>
                <button
                  onClick={() => {
                    setPrintLpo(selectedLpo);
                    setIsPrintModalOpen(true);
                    setSelectedLpo(null);
                  }}
                  className="flex-1 py-3 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold transition-all text-sm flex justify-center items-center gap-2"
                >
                  <Printer className="w-4 h-4 text-blue-200" />
                  <span>Print LPO Document</span>
                </button>
                <button onClick={() => { handleExportLPO_PDF(selectedLpo); setSelectedLpo(null); }} className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-2xl font-bold transition-all text-sm flex justify-center items-center gap-2">
                  <Download className="w-4 h-4" />
                  <span>Download Contract PDF</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* LPO Printout Modal (Formatted identically to user uploaded LPO image) */}
      <LPOPrintModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        lpo={printLpo}
      />
    </div>
  );
};

export default ProcurementPage;
